import { apiValidateDeployRepoBranch, BulkCheckItem} from '../helpers/apiHelpers';
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  ValidatedOptions,
} from '@patternfly/react-core';
import { ServiceDep } from './useFinalDeps';
import { useStableAccessToken } from './useStableAccessToken';

export function useDeployRepoParams(branch: string | undefined = undefined, namespace: string | undefined = undefined, items: BulkCheckItem[] | ServiceDep[] | undefined = undefined) {
  const tokenGetter = useStableAccessToken();
  const [value, setValue] = useState<string>('');
  const [valid, setValid] = useState<ValidatedOptions>(ValidatedOptions.default);
  const [message, setMessage] = useState<string | JSX.Element>('');
  const [validating, setValidating] = useState<boolean>(branch == '' ? false : true);
  const [initialized, setInitialized] = useState<boolean>(true);
  const timer = useRef<number>();
  const validated = useRef<boolean>(false);
  const [selected, setSelected] = useState<string>('');

  useEffect(() => {
    if (branch) {
        onChange(branch);
    }
  }, [branch, items])

  const onBlur = useCallback(async () : Promise<void> => {
    try {
      // These lines prevent from triggering twice when onBlur and timeout happen
      if (timer.current) {
        window.clearTimeout(timer.current);
        timer.current = undefined;
      }
      
      if (validated.current) return
      validated.current = true

      if ((!value ) && (!namespace)) {
        setInitialized(true);
        return;
      }

      setValidating(true);

      const data = await apiValidateDeployRepoBranch(value, tokenGetter, namespace, items);

      setValid(data.exists ? ValidatedOptions.success : ValidatedOptions.error);
    
      if (!value && namespace) {
        if (data.unique) {
          setSelected(data.branch);

          if (!data.exists) {
            setMessage(`Branch ${data.branch} does not exist on Teladoc/deploy repo`)
          }
        } else {
          setSelected('-')
          setMessage(`Cannot determine the correct branch because the apps were deployed using different branches`)
        }
      } else if (value && !namespace) {
        setSelected(value);
        setMessage(data.exists ? '' : `Branch ${value} does not exist on Teladoc/deploy repo. Make sure it is spelled correctly (the field is case sensitive). Leave it blank to use master.`)
      } else if (value && namespace) {
        setSelected(value);
        setValid(!data.exists || !data.unique ? ValidatedOptions.error : ValidatedOptions.success)
        if (!data.unique) {
          setMessage(`Selected apps are going to be overriden with ${value}`)
        } else if (!data.exists) {
          setMessage(`Branch ${value} does not exist on deploy repo. Make sure it is spelled correctly (the field is case sensitive). Leave it blank to use master.`)
        }
      }
    }
    catch (err) {
      setValid(ValidatedOptions.error);
      setMessage(err instanceof Error ? err.message : "Unknown error while trying to validate the branch")
    }
    finally {
      setValidating(false);
      setInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validated, value, namespace, items]) // tokenGetter is stable, don't include in deps

  const onChange = useCallback((value: string) => {
    setValue(value)
    setValid(ValidatedOptions.default)
    validated.current = false
  }, [])

  useEffect(() => {
    if (timer.current) {
      window.clearTimeout(timer.current)
      timer.current = undefined;
    }
    validated.current = false;
    timer.current = window.setTimeout(onBlur, 2000);

    return () => {
      if (timer.current) {
        window.clearTimeout(timer.current);
        timer.current = undefined;
      }
    };
  }, [value, onBlur, namespace, items])
  
  return {value, onChange, onBlur, valid, message, validating, initialized, selected};
};